const LOGO_DEV_IMG_BASE = "https://img.logo.dev";
const LOGO_DEV_SEARCH_BASE = "https://api.logo.dev/search";
const LOGO_DEV_PUBLISHABLE_KEY = import.meta.env.VITE_LOGO_DEV_PUBLISHABLE_KEY || "pk_Zm-m87WGSp6dOBqWyVKQwg";
const LOGO_DEV_SECRET_KEY = import.meta.env.VITE_LOGO_DEV_SECRET_KEY || "";

const DOMAIN_PATTERN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
const brandSearchCache = new Map<string, Promise<string | null>>();

const sanitizeDomain = (value?: string): string => {
  if (!value) return "";
  const cleaned = value.trim().toLowerCase().replace(/^www\./, "").replace(/\/+$/, "");
  return DOMAIN_PATTERN.test(cleaned) ? cleaned : "";
};

export const extractDomain = (value?: string): string => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (trimmed.includes("@")) {
    const fromEmail = sanitizeDomain(trimmed.split("@")[1] || "");
    if (fromEmail) return fromEmail;
  }

  try {
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      const url = new URL(trimmed);
      const fromUrl = sanitizeDomain(url.hostname);
      if (fromUrl) return fromUrl;
    }
  } catch (err) {
    // no-op, continue fallback parsing
  }

  const withoutProtocol = trimmed
    .replace(/^https?:\/\//i, "")
    .split("/")[0]
    .split("?")[0]
    .split("#")[0]
    .replace(/:\d+$/, "");

  return sanitizeDomain(withoutProtocol);
};

export const buildLogoDevUrl = ({
  domain,
  name,
  size = 80,
}: {
  domain?: string;
  name?: string;
  size?: number;
}) => {
  const params = new URLSearchParams({
    size: String(size),
    format: "png",
    fallback: "monogram",
  });

  if (LOGO_DEV_PUBLISHABLE_KEY) {
    params.set("token", LOGO_DEV_PUBLISHABLE_KEY);
  }

  const normalizedDomain = sanitizeDomain(domain);
  if (normalizedDomain) {
    return `${LOGO_DEV_IMG_BASE}/${normalizedDomain}?${params.toString()}`;
  }

  const normalizedName = String(name || "").trim();
  if (!normalizedName) return "";
  return `${LOGO_DEV_IMG_BASE}/name/${encodeURIComponent(normalizedName)}?${params.toString()}`;
};

const parseBrandSearchFirstDomain = (payload: any): string => {
  const first = Array.isArray(payload)
    ? payload[0]
    : (Array.isArray(payload?.results) ? payload.results[0] : null);

  if (!first || typeof first !== "object") return "";

  const candidates = [
    first.domain,
    first.hostname,
    first.website,
    first.website_url,
    first.url,
    first.root_domain,
  ];

  return candidates
    .map((candidate) => extractDomain(String(candidate || "")))
    .find((domain) => !!domain) || "";
};

export const getBrandSearchFirstDomain = async (query?: string): Promise<string | null> => {
  const normalizedQuery = String(query || "").trim();
  if (!normalizedQuery) return null;
  if (!LOGO_DEV_SECRET_KEY) return null;

  const cacheKey = normalizedQuery.toLowerCase();
  if (!brandSearchCache.has(cacheKey)) {
    const requestPromise = (async () => {
      try {
        const response = await fetch(`${LOGO_DEV_SEARCH_BASE}?q=${encodeURIComponent(normalizedQuery)}`, {
          headers: {
            Authorization: `Bearer ${LOGO_DEV_SECRET_KEY}`,
          },
        });
        if (!response.ok) return null;

        const payload = await response.json();
        const domain = parseBrandSearchFirstDomain(payload);
        return domain || null;
      } catch (error) {
        return null;
      }
    })();

    brandSearchCache.set(cacheKey, requestPromise);
  }

  return brandSearchCache.get(cacheKey) || null;
};

export const resolveLogoSource = async ({
  img,
  desc,
  size = 80,
}: {
  img?: string;
  desc?: string;
  size?: number;
}) => {
  if (img) return img;

  const directDomain = extractDomain(desc);
  if (directDomain) {
    return buildLogoDevUrl({ domain: directDomain, size });
  }

  const brandDomain = await getBrandSearchFirstDomain(desc);
  if (brandDomain) {
    return buildLogoDevUrl({ domain: brandDomain, size });
  }

  return buildLogoDevUrl({ name: desc, size });
};

const avatarGenerator = ({
  img,
  desc,
  size,
}: { img?: string; desc?: string; size?: number }) => {
  if (img) return img;

  const domain = extractDomain(desc);
  if (domain) {
    return buildLogoDevUrl({ domain, size: size || 80 });
  }

  return buildLogoDevUrl({ name: desc, size: size || 80 });
};

export default avatarGenerator;
